/**
 * Helpers para exportar SUENLACE A3 y Excel del Libro de IVA agrupando facturas en
 * archivos con distinta fecha contable. Encapsula la lógica que antes vivía dentro de
 * VatBook.tsx para poder generar varios ficheros SUENLACE en un solo ZIP.
 */

import ExcelJS from "exceljs";
import { parseNumericLike } from "@/lib/numberFormatting";
import type { ProcessedFile } from "@/components/InvoiceList";
import type { A3ExportRow } from "@/lib/a3ecoExport";
import { computeSeriesNumbers } from "@/lib/invoiceSeries";

const safeText = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

/** Convierte YYYY-MM-DD a DD-MM-YYYY (formato requerido por las plantillas A3). */
const toDDMMYYYY = (iso: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(safeText(iso));
  return m ? `${m[3]}-${m[2]}-${m[1]}` : safeText(iso);
};

const normalizeNif = (nif: string): string =>
  nif.replace(/[\s.\-]/g, "").toUpperCase().replace(/^ES(?=[A-Z0-9]{9}$)/, "");

const normalizeInvoiceNumber = (value: unknown): string => {
  // Mismo criterio que la exportación SUENLACE: número de factura íntegro.
  return safeText(value).toUpperCase();
};

/** Series number for a file by its index in the ORIGINAL files array (no sort). */
export const seriesNumberFor = (
  fileIndex: number,
  prefix: string,
  startNumber: number,
): string => `${safeText(prefix).toUpperCase()}${startNumber + fileIndex}`;

/**
 * Construye las filas A3ExportRow para un subconjunto de ProcessedFile usando
 * `accountingDate` como fecha del asiento. La numeración de serie respeta el
 * índice global del archivo en `allFiles`, no su posición dentro del archivo.
 */
export const buildA3RowsForFiles = (
  filesInBatch: ProcessedFile[],
  allFiles: ProcessedFile[],
  prefix: string,
  startNumber: number,
  accountingDate: string,
): A3ExportRow[] => {
  const indexByFileId = new Map<string, number>();
  allFiles.forEach((f, idx) => indexByFileId.set(f.id, idx));

  const seriesNumbers = computeSeriesNumbers(allFiles, prefix, startNumber);
  const out: A3ExportRow[] = [];
  for (const f of filesInBatch) {
    const fileIndex = indexByFileId.get(f.id) ?? 0;
    const breakdown =
      f.vatBreakdown && f.vatBreakdown.length > 0
        ? f.vatBreakdown
        : [{ taxableBase: 0, vatRate: 0, vatAmount: 0 }];
    const isLeasing = !!(f.leasingCostRecovery && f.leasingCostRecovery !== 0);
    const extra: { amount: number; account: string; label: string }[] = [];
    if (f.leasingFinancialCharge && f.leasingFinancialCharge !== 0) {
      extra.push({
        amount: f.leasingFinancialCharge,
        account: f.leasingFinancialChargeAccount || "",
        label: "Carga financiera",
      });
    }
    if (f.leasingInsurance && f.leasingInsurance !== 0) {
      extra.push({
        amount: f.leasingInsurance,
        account: f.leasingInsuranceAccount || "",
        label: "Seguro",
      });
    }

    breakdown.forEach((vat, idx) => {
      const lineExpense = (vat as { expenseAccount?: string }).expenseAccount;
      // Cualquier línea sin cuenta propia hereda la del fichero, no solo idx===0.
      // Si no lo hacemos, las líneas a IVA 0% (o cualquier línea adicional sin
      // contrapartida explícita) llegan vacías a A3 y se descartan en
      // buildA3SuenlaceFile → el asiento queda descuadrado.
      const expenseForRow =
        typeof lineExpense === "string" && lineExpense.trim()
          ? lineExpense
          : f.expenseAccountCode || "";
      out.push({
        date: accountingDate || f.date,
        invoiceDate: f.date,
        supplier: f.supplier,
        supplierTaxId: normalizeNif(f.supplierTaxId || ""),
        invoiceNumber: normalizeInvoiceNumber(f.invoiceNumber),
        taxableBase: parseNumericLike(vat.taxableBase),
        vatRate: parseNumericLike(vat.vatRate),
        vatAmount: parseNumericLike(vat.vatAmount),
        irpfRate: idx === 0 ? parseNumericLike(f.irpfRate ?? 0) : null,
        irpfAmount: idx === 0 ? parseNumericLike(f.irpfAmount ?? 0) : null,
        totalAmount: idx === 0 ? parseNumericLike(f.totalAmount ?? 0) : null,
        supplierAccountCode: f.supplierAccountCode || "",
        expenseAccountCode: expenseForRow,
        supplierDescription: f.supplierDescription || "",
        ivaSeriesNumber: seriesNumbers.get(f.id) ?? seriesNumberFor(fileIndex, prefix, startNumber),
        groupKey: f.id,
        leasingExtraLines: idx === 0 ? extra : undefined,
        leasingCostRecovery: idx === 0 && isLeasing ? f.leasingCostRecovery ?? null : null,
        leasingCostRecoveryAccount:
          idx === 0 && isLeasing ? f.leasingCostRecoveryAccount ?? "" : "",
        postalCode: f.postalCode || "",
        paymentEntry:
          idx === 0 && f.generatePaymentEntry && f.paymentEntryAccount
            ? { account: f.paymentEntryAccount, date: accountingDate || f.date, kind: f.paymentEntryKind ?? null }
            : null,
        operationType: f.operationType ?? "interior",
        retentionType: f.retentionType ?? "none",
        vatDeductibility: f.vatDeductibility ?? "full",
        notes: idx === 0 ? f.notes ?? "" : "",
        isCreditNote: idx === 0 ? !!f.isCreditNote : undefined,
      });
    });
  }
  return out;
};

/**
 * Construye el blob de Excel del Libro de IVA con TODAS las facturas, leyendo la
 * fecha contable de cada fila desde `accountingDateByFileId`. Si una factura no
 * está en el mapa, se deja en blanco.
 *
 * El formato (cabeceras, columnas, líneas extra de leasing) replica exactamente
 * el del botón "Descargar Excel" de VatBook para no romper el flujo del usuario.
 */
export const buildLibroIvaExcelBlob = async (
  allFiles: ProcessedFile[],
  prefix: string,
  startNumber: number,
  accountingDateByFileId: Map<string, string>,
): Promise<Blob> => {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("Libro IVA");

  const headers = [
    "Serie/Nº IVA",
    "Fecha",
    "Proveedor",
    "NIF",
    "Nº Factura",
    "Base Imponible",
    "Tipo IVA (%)",
    "Cuota IVA",
    "Tipo IRPF (%)",
    "Cuota IRPF",
    "Total Factura",
    "Forma de Pago",
    "Cuenta Proveedor",
    "Cuenta de Gasto",
    "Descripción",
    "Fecha contable",
    "Deducibilidad IVA",
  ];
  ws.addRow(headers);
  ws.getRow(1).font = { bold: true };

  // Ordena por fecha como hace VatBook.buildRows (oldest first) pero mantiene la
  // numeración de serie según el índice ORIGINAL en allFiles (estable).
  const indexByFileId = new Map<string, number>();
  allFiles.forEach((f, idx) => indexByFileId.set(f.id, idx));
  const seriesNumbers = computeSeriesNumbers(allFiles.filter(Boolean), prefix, startNumber);
  const sorted = [...allFiles]
    .filter(Boolean)
    .sort((a, b) => safeText(a?.date).localeCompare(safeText(b?.date)));

  sorted.forEach((f) => {
    const fileIndex = indexByFileId.get(f.id) ?? 0;
    const seriesNum = seriesNumbers.get(f.id) ?? seriesNumberFor(fileIndex, prefix, startNumber);
    const accDate = toDDMMYYYY(accountingDateByFileId.get(f.id) || "");

    const breakdown =
      Array.isArray(f.vatBreakdown) && f.vatBreakdown.length > 0
        ? f.vatBreakdown
        : [{ taxableBase: 0, vatRate: 0, vatAmount: 0 }];

    const hasCostRecovery = !!(f.leasingCostRecovery && f.leasingCostRecovery !== 0);
    const extraLines: { amount: number; account: string; label: string }[] = [];
    if (f.leasingFinancialCharge && f.leasingFinancialCharge !== 0) {
      extraLines.push({
        amount: f.leasingFinancialCharge,
        account: f.leasingFinancialChargeAccount || "",
        label: "Carga financiera",
      });
    }
    if (f.leasingInsurance && f.leasingInsurance !== 0) {
      extraLines.push({
        amount: f.leasingInsurance,
        account: f.leasingInsuranceAccount || "",
        label: "Seguro",
      });
    }

    breakdown.forEach((vat, vatIdx) => {
      const lineExpense = (vat as { expenseAccount?: string }).expenseAccount;
      const expenseForRow =
        typeof lineExpense === "string" && lineExpense.trim()
          ? lineExpense
          : f.expenseAccountCode || "";
      const taxBase = parseNumericLike(vat?.taxableBase);
      const vatRate = parseNumericLike(vat?.vatRate);
      const vatAmount = parseNumericLike(vat?.vatAmount);
      const irpfRate = vatIdx === 0 ? parseNumericLike(f.irpfRate ?? 0) : "";
      const irpfAmount = vatIdx === 0 ? parseNumericLike(f.irpfAmount ?? 0) : "";
      const totalAmount = vatIdx === 0 ? parseNumericLike(f.totalAmount ?? 0) : "";
      const paymentMethod = vatIdx === 0 ? f.paymentMethod || "" : "";
      const deductibilityLabel =
        f.vatDeductibility === "none"
          ? "No deducible 100%"
          : f.vatDeductibility === "half"
          ? "Deducible 50%"
          : "";

      ws.addRow([
        seriesNum,
        safeText(f.date),
        safeText(f.supplier),
        normalizeNif(f.supplierTaxId || ""),
        normalizeInvoiceNumber(f.invoiceNumber),
        hasCostRecovery && vatIdx === 0 ? f.leasingCostRecovery : taxBase,
        vatRate,
        vatAmount,
        irpfRate,
        irpfAmount,
        totalAmount,
        paymentMethod,
        f.supplierAccountCode || "",
        hasCostRecovery && vatIdx === 0
          ? f.leasingCostRecoveryAccount || ""
          : expenseForRow,
        f.supplierDescription || "",
        accDate,
        deductibilityLabel,
      ]);

      // Las líneas extra de leasing se añaden una sola vez tras la primera línea de IVA
      if (vatIdx === 0) {
        extraLines.forEach((l) => {
          ws.addRow([
            seriesNum,
            safeText(f.date),
            safeText(f.supplier),
            normalizeNif(f.supplierTaxId || ""),
            normalizeInvoiceNumber(f.invoiceNumber),
            l.amount,
            "",
            "",
            "",
            "",
            "",
            paymentMethod,
            f.supplierAccountCode || "",
            l.account,
            f.supplierDescription || "",
            accDate,
            deductibilityLabel,
          ]);
        });
      }
    });
  });

  // Fila de TOTALES: suma Base Imponible, Cuota IVA, Cuota IRPF y Total Factura.
  const lastDataRow = ws.rowCount; // última fila de datos (cabecera = 1)
  if (lastDataRow >= 2) {
    const totalsRow = ws.addRow([
      "",
      "",
      "",
      "",
      "TOTALES",
      { formula: `SUM(F2:F${lastDataRow})` },
      "",
      { formula: `SUM(H2:H${lastDataRow})` },
      "",
      { formula: `SUM(J2:J${lastDataRow})` },
      { formula: `SUM(K2:K${lastDataRow})` },
      "",
      "",
      "",
      "",
      "",
      "",
    ]);
    totalsRow.font = { bold: true };
    ["F", "H", "J", "K"].forEach((c) => {
      totalsRow.getCell(c).numFmt = "#,##0.00";
    });
  }

  // Formato numérico para las columnas de importes en las filas de datos.
  for (let r = 2; r <= lastDataRow; r++) {
    ["F", "H", "J", "K"].forEach((c) => {
      const cell = ws.getRow(r).getCell(c);
      if (typeof cell.value === "number") cell.numFmt = "#,##0.00";
    });
  }

  ws.columns.forEach((col) => {
    col.width = 16;
  });

  const buf = await wb.xlsx.writeBuffer();
  return new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};
